import React from 'react';
import ReactMapboxGl, { Feature, ZoomControl } from 'react-mapbox-gl';
import * as MapboxGl from 'mapbox-gl';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { union, includes, isEqual, parseInt } from 'lodash';

import GlobalWrapper from './GlobalStyle';
import Button from './styles/Button';
import icons from './styles/mixin/icons';

import createMapImage from '../utils/createMapImage';
import pushToDataLayer from '../utils/pushToDataLayer';

import device, { size as deviceSize, isTouchDevice } from '../constants/device';
import FiltersControl from './Map/FiltersControl';
import EmbedOverlay from './Map/EmbedOverlay';
import Popup from './Map/Popup';
import { AreasLayer, DotsLayer, PinsLayer, ActivePinLayer, DomainLayer } from './Map/Layers';
import { fitBoundsRotated } from '../utils/mapboxFitBoundsRotated';

const rootHTML = document.documentElement;
// //////////////////////////////////////////////////////////////////////////////////
// Create new Mapbox constant
const MapBox = ReactMapboxGl({
    attributionControl: false,
    minZoom: 14,
    maxZoom: 17.4,
});
const zoomThreshold = 15;
const initalZoomLevel = 14.9;
const mapCenter = [5.047667, 51.649237];

// Setting some image fallbacks for markers
const imageFallback = createMapImage(require('../img/marker_default.png'));
const imageActiveFallback = createMapImage(require('../img/marker_default-active.png'));
// Badges for the 4 areas in the Efteling World
const areaBadges = [
    {
        id: 'poi://eftelingpark',
        name: 'Efteling Park',
        category: 'park',
        coordinates: {
            longitude: 5.047609,
            latitude: 51.649123,
        },
    },
    {
        id: 'poi://eftelinghotel',
        name: 'Efteling Hotel',
        category: 'overnight',
        coordinates: {
            longitude: 5.054541,
            latitude: 51.652841,
        },
    },
    {
        id: 'poi://bosrijk',
        name: 'Efteling Bosrijk',
        category: 'overnight',
        coordinates: {
            longitude: 5.040252,
            latitude: 51.644856,
        },
    },
    {
        id: 'poi://loonscheland',
        name: 'Efteling Loonsche Land',
        category: 'overnight',
        coordinates: {
            longitude: 5.049307,
            latitude: 51.644606,
        },
    },
];

const fullscreenHeight = '100%';
const inlineHeight = '550px';
let mapbox = null;
let firstFullscreen = true;
let popupTimeout;

// START OF CUSTOM STYLING //
const StyledMapBox = styled(MapBox)`
    @media ${device.mobile} and (orientation: landscape) {
        max-height: ${props => (props.fullscreen ? 'auto' : '100vh')};
    }
    @media ${device.mobile} and (orientation: portrait) {
        max-height: ${props => (props.fullscreen ? 'auto' : '75vh')};
    }
`;

const FilterButton = styled(Button)`
    position: absolute;
    top: 10px;
    left: 10px;
    color: ${props => props.theme.colors.brown};
    @media ${device.mobile} {
        display: none;
    }
`;

const FullscreenBtn = styled(Button)`
    position: absolute;
    min-width: 44px;
    height: 44px;
    top: 10px;
    right: 10px;
    color: ${props => props.theme.colors.brown};
    z-index: 2;
`;

const CloseBtn = styled.span`
    padding-right: ${props => (props.fullscreen ? '25px' : '')};
    &::before {
        ${icons}
        content: ${props => (props.fullscreen ? props.theme.icons.expand : props.theme.icons.close)};
        font-size: 16px;
        position: absolute;
        top: 50%;
        left: ${props => (props.fullscreen ? 'auto' : '50%')};
        right: ${props => (props.fullscreen ? '5px' : 'auto')};
        transform: translate(-50%, -50%);
    }
`;

const StyledZoomControl = styled(ZoomControl)`
    z-index: 2 !important;
    button {
        position: relative;
        background-image: none !important;
        &::before {
            ${icons}
            content: ${props => props.theme.icons.min};
            font-size: 12px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
        }
        &#zoomIn::before {
            content: ${props => props.theme.icons.plus};
        }
    }
    @media ${device.mobile} {
        display: none !important;
    }
`;
// END OF CUSTOM STYLING //

const hasPoiType = (type, value) => poi => {
    if (['audiences', 'accessibility_features'].includes(type)) {
        return poi[type].find(t => t.id === value);
    }
    if (type === 'minimal_length') {
        return poi[type] < parseInt(value);
    }
    if (type === 'area') {
        return poi[type].id === value;
    }
    return false;
};
const isValidSubCat = sub => !!(sub && sub.type && sub.value);
const filteredPois = (pois, cat, subcat) => {
    const filteredPoiByCat = pois.filter(poi => poi.category === cat);
    return isValidSubCat(subcat) ? filteredPoiByCat.filter(hasPoiType(subcat.type, subcat.value)) : filteredPoiByCat;
};
const filteredPoiIds = (pois, cat, subcat) => filteredPois(pois, cat, subcat).map(poi => poi.id);
const prefixedPoiId = selectedPoi => (selectedPoi ? `poi://${selectedPoi}` : null);
const poiById = (pois, selectedId) => pois.find(({ id }) => selectedId === id);
const initialCenter = (pois, selectedPoi) => {
    const selectedPoiId = prefixedPoiId(selectedPoi);
    const poi = poiById(pois, selectedPoiId);
    return poi ? [poi.coordinates.longitude, poi.coordinates.latitude] : mapCenter; // Set center to Efteling Park;
};

export default class Map extends React.Component {
    state = {
        center: initialCenter(this.props.pois, this.props.selectedPoi),
        zoom: this.props.selectedPoi ? [16.3] : [16], // initial zoom
        selectedPoiId: prefixedPoiId(this.props.selectedPoi),
        popupActive: !!this.props.selectedPoi,
        smallPopup: !!this.props.selectedPoi,
        styleUrl: this.props.styleUrl,
        selectedFilters: this.props.selectedFilters || {},
        embedActive: !!(!this.props.fullscreen && this.props.embed),
        viewport: {
            width: '100%',
            height: this.props.fullscreen ? fullscreenHeight : inlineHeight,
        },
    };

    get mapboxOptions() {
        return {
            style: this.state.styleUrl,
            center: this.state.center,
            zoom: this.state.zoom,
            // Set bounds for the whole Efteling World
            maxBounds: [
                [5.012705, 51.607638], // Southwest coordinates
                [5.080772, 51.689828], // Northeast coordinates
            ],
            bearing: [90],
            containerStyle: {
                height: this.state.viewport.height,
                width: this.state.viewport.width,
            },
        };
    }

    // Setting a different mouse cursor on hovering a POI
    onMouseEnter = event => {
        event.map.getCanvas().style.cursor = 'pointer';
    };

    // Back to the normal mouse cursor when leaving a POI
    onMouseLeave = event => {
        event.map.getCanvas().style.cursor = '';
    };

    // Create a layer bounds of all active POIs
    boundToLayer = pois => {
        let coords = [];
        const zoomDuration = 1000;

        // Create a list of all longitudes/latitudes of the active POIs
        coords = pois.map(poi =>
            poi.coordinates.longitude ? [poi.coordinates.longitude, poi.coordinates.latitude] : false
        );
        // Create a layer bounds from this list
        const layerBounds = coords.reduce(
            (layerBound, coord) => layerBound.extend(coord),
            new MapboxGl.LngLatBounds(coords[0], coords[0])
        );

        // Check if the layer bounds is valid
        if (!layerBounds.isEmpty()) {
            let mapPadding = { top: 40, bottom: 20, left: 180, right: 40 };

            // Changed the padding for mobile devices
            if (window.outerWidth < deviceSize.tablet) {
                mapPadding = { top: 20, bottom: 20, left: 20, right: 20 };
            }

            const fittedMap = mapbox.fitBoundsRotated(layerBounds, {
                bearing: 90,
                maxZoom: 16.5,
                padding: mapPadding,
                duration: zoomDuration,
            });

            // Prevent the map to get beyond the zoom threshold
            setTimeout(() => {
                if (fittedMap.getZoom() < zoomThreshold) {
                    fittedMap.zoomTo(zoomThreshold);
                }
            }, zoomDuration);
        }
    };

    // Fullscreen active/deactive functionality
    onFullscreen = () => {
        const { fullscreen, embed, setFullscreen } = this.props;
        let height;
        // Check if we're in fullscreen modusn
        if (fullscreen) {
            rootHTML.classList.remove('modal-open');
            // Set the correct inline height for CSS
            height = inlineHeight;
            // Disable the scroll zoom funcionality
            mapbox.scrollZoom.disable();
            // If it's a touchdevice, prevent dragging/panning with finger
            if (isTouchDevice()) {
                mapbox.dragPan.disable();
            }
            // Setting the correct states
            this.setState({ embedActive: embed, popupActive: false });
            if (embed && this.state.selectedPoiId) {
                this.setState({ selectedPoiId: null });
            }
        } else {
            rootHTML.classList.add('modal-open');
            // Set the correct fullscreen height for CSS
            height = fullscreenHeight;
            // Enable the scroll zoom funcionality
            mapbox.scrollZoom.enable();
            // If it's a touchdevice, enable dragging/panning with finger
            if (isTouchDevice()) mapbox.dragPan.enable();
            // Setting the correct states
            this.setState({ embedActive: false });
            // Only zoom to inital zoomlevel when hitting the first fullscreen
            if (firstFullscreen) {
                firstFullscreen = false;
                // Zoom to initial zoom level
                setTimeout(() => {
                    mapbox.flyTo({ mapCenter, zoom: initalZoomLevel, speed: 0.5 });
                }, 500);
            }
        }

        setFullscreen(!fullscreen);

        this.setState({ viewport: { width: '100%', height } }, () => mapbox.resize());

        // Added push to datalayer event for fullscreen event
        pushToDataLayer('map', 'fullscreen', fullscreen ? 'deactivate' : 'activate');
    };

    matchExpression = (next = [], prev = [], up = 1, down = 0) =>
        union(next, prev) // Remove duplicate pois
            .reduce(
                (accumulator, current) => {
                    // Determin change value by checking if current (poi) exsists in next (poi array)
                    const change = includes(next, current) ? up : down;
                    return [...accumulator, current, change];
                },
                ['default', 1] // Add default id to prevent min of one to paint
            );

    // Setting the circle layer POIs
    setCircleLayerByIds = pois => {
        mapbox.setFilter('circle-layer', ['!in', 'POI_ID', ...pois]);
    };

    // Setting the pin layer POIs
    setPinLayerByIds = (pois, prevPois = []) => {
        if (rootHTML.classList.contains('ie')) {
            mapbox.setFilter('pin-layer', ['in', 'POI_ID', ...pois]);
            mapbox.setPaintProperty('pin-layer', 'icon-opacity', 1);
            mapbox.setLayoutProperty('pin-layer', 'icon-size', 1);
        } else {
            // Setting a constant number to change
            const changeNumber = 0.1;
            // Setting some default values used in the animation
            let changeUp = 0;
            let changeDown = 1;

            const pinIconAnimation = () => {
                const opacityExpression = [
                    'match',
                    ['get', 'POI_ID'],
                    ...this.matchExpression(pois, prevPois, changeUp, changeDown),
                    0,
                ];
                // First set the opacity of the POIs in the new and prev category
                // and set all the other POIs to opacity 1
                mapbox.setPaintProperty('pin-layer', 'icon-opacity', opacityExpression);
                // Set the correct size of the symbol icon
                mapbox.setLayoutProperty('pin-layer', 'icon-size', opacityExpression);
                // Changing the numbers for the animation
                changeUp = Math.round((changeUp + changeNumber) * 1e2) / 1e2;
                changeDown = Math.round((changeDown - changeNumber) * 1e2) / 1e2;

                // Stop the animation if we reach 1
                if (changeUp > 1.0) {
                    return true;
                }

                requestAnimationFrame(pinIconAnimation);
            };
            pinIconAnimation();
        }
    };

    // Animate the active marker based on enable it or disable it
    animateActiveMarker = (enable = true) => {
        const opacity = enable ? 1 : 0;
        if (rootHTML.classList.contains('ie')) {
            mapbox.setPaintProperty('active-layer', 'icon-opacity', opacity);
            mapbox.setLayoutProperty('active-layer', 'icon-size', 1.2);
        } else {
            const changeNumber = 0.01;
            const startValue = 1.0;
            const endValue = 1.2;
            let iconSize = enable ? startValue : endValue;

            // Set the icon-opacity to be either visible (1) or invisible (0)
            mapbox.setPaintProperty('active-layer', 'icon-opacity', opacity);
            const activeMarkerAnimation = () => {
                mapbox.setLayoutProperty('active-layer', 'icon-size', iconSize);
                if (enable) {
                    iconSize += changeNumber;
                    if (iconSize.toFixed(2) >= endValue) {
                        return true;
                    }
                } else {
                    iconSize -= changeNumber;
                    if (iconSize.toFixed(2) <= startValue) {
                        return true;
                    }
                }

                requestAnimationFrame(activeMarkerAnimation);
            };
            activeMarkerAnimation();
        }
    };

    onStyleLoad = map => {
        const { onStyleLoad, pois, fullscreen } = this.props;
        const { category, subCategory } = this.state.selectedFilters;
        mapbox = fitBoundsRotated(map);

        // disable map rotation using right click + drag
        mapbox.dragRotate.disable();
        // disable map rotation using touch rotation gesture
        mapbox.touchZoomRotate.disableRotation();
        // Animate the active marker if selectedPoi is set as property
        this.animateActiveMarker();
        // Set all other POI markers based on active category
        this.setCircleLayerByIds(filteredPoiIds(pois, category, subCategory));
        this.setPinLayerByIds(filteredPoiIds(pois, category, subCategory));

        if (fullscreen) {
            // enable map scroll zoom if in fullscreen mode
            mapbox.scrollZoom.enable();
            if (isTouchDevice()) mapbox.dragPan.enable();
        } else {
            // disable map scroll zoom if not in fullscreen mode
            mapbox.scrollZoom.disable();
            if (isTouchDevice()) mapbox.dragPan.disable();
        }

        mapbox.on('zoom', this.zoomHandler);
        mapbox.on('zoomend', this.zoomEndHandler);

        // When the device orientation changes, do a resize of the map
        window.addEventListener('orientationchange', function() {
            mapbox.resize();
        });

        return onStyleLoad && onStyleLoad(map);
    };

    zoomHandler = () => {
        const zoomThresholdReached = mapbox.getZoom() <= zoomThreshold;
        const { popupActive, selectedPoiId } = this.state;
        if (zoomThresholdReached && popupActive) {
            this.setState({ popupActive: false });
        } else if (!zoomThresholdReached && !popupActive && selectedPoiId) {
            this.setState({ popupActive: true });
        }
    };

    zoomEndHandler = () => {
        // Added push to datalayer event for zoom
        pushToDataLayer('map', 'zoom', mapbox.getZoom());
    };

    onStyleImageMissing = (map, e) => {
        // id of the missing image
        const imageId = e.id;
        if (!mapbox.hasImage(imageId)) {
            if (imageId.startsWith('marker_active')) {
                // Add an active fallback marker image
                mapbox.addImage(imageId, imageActiveFallback, { pixelRatio: 2 });
            } else {
                // Add an normal fallback marker image
                mapbox.addImage(imageId, imageFallback, { pixelRatio: 2 });
            }
        }
    };

    // Deactive markers / popups when clicked on the map
    onMapClick = () => {
        if (this.state.selectedPoiId) {
            // Disabling the active marker
            this.animateActiveMarker(false);
            // Setting the correct states and set a timeout to
            // first animate the POI before setting the state
            this.setState({ popupActive: false }, () => {
                popupTimeout = setTimeout(() => {
                    this.setState(() => ({ selectedPoiId: null, smallPopup: false }));
                }, 300);
            });
        }
    };

    onPoiClick = poi => {
        if (this.state.selectedPoiId === poi.id) {
            return false;
        }
        clearTimeout(popupTimeout);
        // Move the center 100 down for better Popup display
        const poiPos = mapbox.project([poi.coordinates.longitude, poi.coordinates.latitude]);
        // Only move the Y position down when:
        // it is not an AreaBadge which is clicked or not on mobile view
        if (!(Object.values(areaBadges).indexOf(poi) > -1) && window.outerWidth >= deviceSize.tablet) {
            poiPos.y -= 100;
        }
        this.animateActiveMarker();
        const center = mapbox.unproject(poiPos);
        this.setState({ center, selectedPoiId: poi.id, popupActive: true, smallPopup: false }, () => {
            const flyToZoomLevel = mapbox.getZoom() < 16.5 ? 16.5 : mapbox.getZoom();
            return mapbox.flyTo({ center, zoom: flyToZoomLevel, speed: 0.25 });
        });

        // Added push to datalayer event for clicked poi
        const eventLabel = `${poi.category}:${poi.name}`;
        pushToDataLayer('map', 'poi open', eventLabel.toLowerCase());
    };

    onPoiMoreInfoClick = poi => {
        if (poi.more_info_web_link) {
            // Added push to datalayer event for clicked poi more link
            const eventLabel = `${poi.category}:${poi.name}`;
            pushToDataLayer('map', 'poi more info', eventLabel.toLowerCase());
            window.location = poi.more_info_web_link;
        }
    };

    poiIds = pois => pois.reduce((acc, cur) => [...acc, cur.id], []);

    setSelectedFilters = selectedFilters => {
        const { category: prevCategory, subCategory: prevSubCategory } = this.state.selectedFilters;
        const { pois: allPois } = this.props;
        const { category, subCategory } = selectedFilters;

        const prevPoiIds = filteredPoiIds(allPois, prevCategory, prevSubCategory);
        const poiIds = filteredPoiIds(allPois, category, subCategory);
        const pois = filteredPois(allPois, category, subCategory);

        this.setState({ selectedFilters, selectedPoiId: null, smallPopup: false }, () => {
            this.boundToLayer(pois);
            if (!isEqual(poiIds, prevPoiIds)) {
                this.setPinLayerByIds(poiIds, prevPoiIds);
                this.setCircleLayerByIds(poiIds); // remove dots by category
            }
        });

        // Added push to datalayer event for filter clicks
        let eventAction = category ? 'category expand' : 'category collapse';
        let eventLabel = category || prevCategory;
        if (subCategory) {
            eventAction = 'filter';
            eventLabel = `${category}:${subCategory.value}`;
        }
        pushToDataLayer('map', eventAction, eventLabel.toLowerCase());
    };

    renderFeature = poi => (
        <Feature
            key={poi.id}
            coordinates={[poi.coordinates.longitude, poi.coordinates.latitude]}
            properties={{
                POI_ID: poi.id,
                CATEGORY: poi.category,
                NAME: poi.name,
            }}
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            onClick={() => this.onPoiClick(poi)}
        />
    );

    renderPopup() {
        const { selectedPoiId, popupActive, smallPopup } = this.state;
        const poi = poiById(this.props.pois, selectedPoiId);
        return (
            poi && (
                <Popup
                    active={popupActive}
                    small={smallPopup}
                    poi={poi}
                    onClick={() => this.onPoiMoreInfoClick(poi)}
                    urlText={this.props.poiUrlText}
                />
            )
        );
    }

    render() {
        const {
            fullscreen,
            pois,
            filterbarOpenButton,
            embed,
            embedTitle,
            embedButtonTitle,
            fullscreenButton,
        } = this.props;
        const { selectedPoiId, selectedFilters, embedActive } = this.state;
        return (
            <GlobalWrapper>
                {embed && !fullscreen && (
                    <EmbedOverlay title={embedTitle} buttonTitle={embedButtonTitle} buttonOnClick={this.onFullscreen} />
                )}
                <StyledMapBox
                    onStyleLoad={this.onStyleLoad}
                    onStyleImageMissing={this.onStyleImageMissing}
                    onClick={this.onMapClick}
                    {...this.mapboxOptions}
                    fullscreen={fullscreen}
                >
                    <DomainLayer zoomThreshold={zoomThreshold}>
                        {pois.filter(poi => poi.category === 'domain').map(this.renderFeature)}
                    </DomainLayer>

                    {/* Mosquite Bumps Layer, this layer renders the four overview labels on the map */}
                    <AreasLayer zoomThreshold={zoomThreshold}>{areaBadges.map(this.renderFeature)}</AreasLayer>

                    {/* Bullet Layer, this layer renders the small icons on the map */}
                    <DotsLayer zoomThreshold={zoomThreshold}>
                        {pois.filter(poi => poi.category !== 'domain').map(this.renderFeature)}
                    </DotsLayer>

                    {/* Pin Layer, this layer renders the larger icons on the map for the active category */}
                    <PinsLayer zoomThreshold={zoomThreshold}>{pois.map(this.renderFeature)}</PinsLayer>

                    {/* Active Pin Layer, this layer renders the larger icons on the map for the active POI */}
                    <ActivePinLayer zoomThreshold={zoomThreshold} poiId={selectedPoiId || false}>
                        {pois.map(this.renderFeature)}
                    </ActivePinLayer>

                    {this.renderPopup()}
                    {!embedActive && <StyledZoomControl position="bottom-right" />}
                </StyledMapBox>

                {!embedActive && !fullscreen && (
                    <FilterButton type="button" onClick={this.onFullscreen}>
                        {filterbarOpenButton}
                    </FilterButton>
                )}

                {!embedActive && (
                    <FullscreenBtn type="button" onClick={this.onFullscreen}>
                        {fullscreen && <CloseBtn />}
                        {!fullscreen && (
                            <CloseBtn fullscreen>
                                <span>{fullscreenButton}</span>
                            </CloseBtn>
                        )}
                    </FullscreenBtn>
                )}

                {/* Filter let you select category or sub category, changes the opacity of the features */}
                <FiltersControl
                    selected={selectedFilters}
                    onClick={this.setSelectedFilters}
                    fullscreen={fullscreen}
                    title={this.props.filterbarTitle}
                    openButton={this.props.filterbarOpenButton}
                    closeButton={this.props.filterbarCloseButton}
                    saveButton={this.props.filterbarSaveButton}
                />
            </GlobalWrapper>
        );
    }
}

Map.propTypes = {
    pois: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            short_description: PropTypes.string,
            description: PropTypes.string,
            category: PropTypes.string.isRequired,
            coordinates: PropTypes.shape({
                latitude: PropTypes.number.isRequired,
                longitude: PropTypes.number.isRequired,
            }).isRequired,
            audiences: PropTypes.array.isRequired,
            accessibility_features: PropTypes.array.isRequired,
            area: PropTypes.object,
            cover: PropTypes.shape({
                src: PropTypes.string.isRequired,
            }),
            more_info_web_link: PropTypes.string,
        })
    ).isRequired,
    filterbarTitle: PropTypes.string.isRequired,
    styleUrl: PropTypes.string.isRequired,
    filterbarOpenButton: PropTypes.string.isRequired,
    filterbarCloseButton: PropTypes.string.isRequired,
    filterbarSaveButton: PropTypes.string.isRequired,
    poiUrlText: PropTypes.string.isRequired,
    selectedFilters: PropTypes.shape({
        category: PropTypes.string.isRequired,
        subCategory: PropTypes.shape({
            type: PropTypes.string,
            value: PropTypes.string,
        }),
    }),
    selectedPoi: PropTypes.string,
    activeCategory: PropTypes.string,
    fullscreen: PropTypes.bool.isRequired,
    fullscreenButton: PropTypes.string.isRequired,
    setFullscreen: PropTypes.func.isRequired,
    embed: PropTypes.bool,
    embedTitle: PropTypes.string,
    embedButtonTitle: PropTypes.string,
    onStyleLoad: PropTypes.func,
};
